import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { postStripeCheckCoupon } from "../../../api/payment/paymentServices";

interface CouponFieldProps {
  /** Called with the backend success payload when a coupon is applied, or null when cleared/failed. */
  onApplied?: (data: any | null) => void;
}

export const CouponField = ({ onApplied }: CouponFieldProps) => {
  const { t } = useTranslation();
  const [coupon, setCoupon] = useState("");
  const [checking, setChecking] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear the applied coupon in the parent when this field is hidden/unmounted.
  useEffect(() => () => onApplied?.(null), []);

  const handleChange = (value: string) => {
    setCoupon(value.toUpperCase());
    if (error) setError(null);
    if (applied) {
      setApplied(false);
      onApplied?.(null);
    }
  };

  const checkCoupon = async () => {
    const code = coupon.trim();
    if (!code || checking) return;
    try {
      setChecking(true);
      setError(null);
      const { data } = await postStripeCheckCoupon(code);
      setApplied(true);
      onApplied?.(data);
    } catch (e: any) {
      setApplied(false);
      onApplied?.(null);
      setError(e?.response?.data?.error || e?.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-start text-sm font-medium text-gray-700">
        {t("subscription.couponLabel")}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={coupon}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={t("subscription.couponPlaceholder")}
          disabled={applied}
          className={`flex-1 rounded-xl border bg-gray-50 px-4 py-2.5 text-sm font-medium tracking-wider text-gray-900 outline-none transition focus:bg-white disabled:opacity-60 ${
            error
              ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
              : applied
                ? "border-green-400"
                : "border-gray-200 focus:border-[#020637] focus:ring-4 focus:ring-[#020637]/10"
          }`}
        />
        <button
          type="button"
          onClick={checkCoupon}
          disabled={checking || !coupon.trim() || applied}
          className="rounded-xl bg-[#020637] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#0e1c7a] disabled:opacity-50"
        >
          {checking
            ? t("subscription.couponChecking")
            : t("subscription.couponApply")}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-sm font-medium text-red-500">{error}</p>
      )}
    </div>
  );
};
